import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { blocks, sectionHeaders, contextualBlocks, attributeDisplayNames } from '../../components/editors/spec/blocks/blockDefinitions'

// Helper function to find block definition based on node attributes
function findBlockDefinition(node) {
    // Check if it's a title
    if (node.attrs.level === 1 && node.type.name === 'heading') {
        const titleBlock = blocks.find(b => b.id === 'title');
        return titleBlock;
    }
    
    // Check if it's a section header
    if (node.attrs.sectionType && node.type.name === 'heading') {
        const sectionBlock = sectionHeaders.find(h => h.id === node.attrs.sectionType);
        return sectionBlock;
    }
    
    // Check if it's a contextual block
    if (node.attrs.context && node.attrs.blockType) {
        const contextBlocks = contextualBlocks[node.attrs.context] || [];
        const contextBlock = contextBlocks.find(b => b.id === node.attrs.blockType);
        return contextBlock;
    }
    
    // Check regular blocks
    const regularBlock = blocks.find(b => b.id === node.attrs.blockType);
    
    // If no block definition found but it's a heading, return a default heading definition
    if (!regularBlock && node.type.name === 'heading') {
        return {
            displayAttributes: ['nodeType', 'level']
        };
    }
    
    return regularBlock;
}

// Helper function to get display value for a property
function getDisplayValue(key, value) {
    // Convert value to string if it's not already
    const stringValue = String(value);
    
    // Special handling for nodeType values
    if (key === 'nodeType') {
        return stringValue === 'heading' ? 'Heading' : 'Paragraph';
    }
    
    // For other values, just capitalize first letter and return
    return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
}

// Create a global function to handle info icon clicks
window.showBlockInfo = function(nodeType, attrs) {
    console.log('showBlockInfo called with:', nodeType, attrs);
    
    // Close any existing popups and their event listeners
    const existingPopups = document.querySelectorAll('.block-info-popup');
    existingPopups.forEach(popup => {
        popup.remove();
        if (popup._clickHandler) {
            document.removeEventListener('click', popup._clickHandler);
        }
    });
    
    try {
        // Parse the attributes
        const parsedAttrs = typeof attrs === 'string' ? JSON.parse(attrs) : attrs;
        
        // Get block properties
        const props = {
            nodeType: nodeType,
            ...parsedAttrs
        };
        
        console.log('Parsed properties:', props);
        
        // Find block definition to get displayAttributes
        const node = { type: { name: nodeType }, attrs: parsedAttrs };
        const blockDef = findBlockDefinition(node);
        console.log('Block definition:', blockDef);
        const displayAttributes = blockDef?.displayAttributes || ['nodeType'];
        
        // Get the click event
        const eventObj = window.event;
        
        // Create and style the popup
        const popup = document.createElement('div');
        popup.className = 'block-info-popup';
        popup.style.position = 'absolute';
        popup.style.left = `${eventObj.pageX + 10}px`;
        popup.style.top = `${eventObj.pageY + 10}px`;
        popup.style.zIndex = 9999;
        popup.style.background = 'var(--color-spec-info-box-bg)';
        popup.style.padding = '10px';
        popup.style.borderRadius = '4px';
        popup.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        popup.style.fontSize = '12px';
        popup.style.color = '#333';
        
        // Format and display only the specified properties with friendly names
        const content = displayAttributes
            .filter(key => props[key] !== null && props[key] !== undefined)
            .map(key => {
                const displayName = attributeDisplayNames[key] || key;
                const displayValue = getDisplayValue(key, props[key]);
                return `<div><strong>${displayName}:</strong> ${displayValue}</div>`;
            })
            .join('');
        
        // Add close button
        const closeButton = `<div style="text-align: right; margin-top: 8px;">
            <button style="background: var(--color-spec-figure-text); border: none; color: white; padding: 2px 8px; border-radius: 4px; cursor: pointer;" 
                    onclick="this.parentNode.parentNode.remove()">Close</button>
        </div>`;
        
        popup.innerHTML = content + closeButton;
        console.log('Popup content set:', popup.innerHTML);
        
        // Add to document
        document.body.appendChild(popup);
        console.log('Popup added to document');
        
        // Add document click handler to close popup
        setTimeout(() => {
            const clickHandler = (e) => {
                if (!popup.contains(e.target)) {
                    popup.remove();
                    document.removeEventListener('click', clickHandler);
                }
            };
            // Store the handler reference on the popup for cleanup
            popup._clickHandler = clickHandler;
            document.addEventListener('click', clickHandler);
        }, 100);
        
    } catch (error) {
        console.error('Error in showBlockInfo:', error);
    }
};


export const BlockInfo = Extension.create({
    name: 'blockInfo',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('blockInfo'),
                props: {
                    decorations: (state) => {
                        const { doc } = state
                        const decorations = []

                        doc.descendants((node, pos) => {
                            if (node.type.name === 'paragraph' || node.type.name === 'heading') {
                                // Create info button
                                const infoIcon = document.createElement('button');
                                infoIcon.className = 'block-info-button';
                                infoIcon.innerHTML = '<i class="fas fa-info-circle"></i>';
                                infoIcon.setAttribute('title', 'Show block info');
                                
                                // Stringify the attributes for passing to the global function
                                const attrsJson = JSON.stringify(node.attrs);
                                const nodeTypeStr = node.type.name;
                                
                                // Store data for the click handler in the editor
                                infoIcon.setAttribute('data-node-type', nodeTypeStr);
                                infoIcon.setAttribute('data-node-attrs', attrsJson.replace(/'/g, "\\'"));
                                
                                // Add the icon at the start of the node
                                const contentPos = pos + 1;
                                
                                // Add the node decoration
                                const blockStart = pos;
                                const blockEnd = pos + node.nodeSize;
                                
                                decorations.push(
                                    Decoration.widget(contentPos, infoIcon, {
                                        side: -1,
                                    })
                                );
                                
                                // Add a class to the block itself for hover detection
                                decorations.push(
                                    Decoration.node(blockStart, blockEnd, {
                                        class: 'hoverable-block'
                                    })
                                );
                            }
                        });

                        return DecorationSet.create(doc, decorations);
                    }
                }
            })
        ]
    }
});